/* eslint-disable prefer-template */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import InputOTP from '@onefifteen-z/react-input-otp';
import { Button, Form } from 'semantic-ui-react';
import IconLogin from 'Assets/images/manadr-rectangle.svg';
import classNames from 'classnames';
import useInterval from 'Hooks/useInterval';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'Redux/reducer';
import WarningIcon from 'Assets/images/commons/warning.svg';
import {
  authResendOTPRequest,
  iamLoginWithLimitedDevices,
  iamVerifyPhoneRequest,
} from 'Redux/modules/auth/actions';
import { VerifyOTPBody } from 'Types';
import { OtpMethod, otpMethodOptions } from 'Containers/Auth/constants';
import { addModal } from 'Redux/modules/modal/actions';
import IconSecure from 'Assets/images/cyber-security.png';
import OtpWarningModal from 'Containers/Auth/OtpWarningModal';

import styles from './Login.less';
import { OtpData } from './Login';

const formatTime = sec => {
  const secNum = parseInt(sec, 10); // don't forget the second param
  const minutes = ('0' + Math.floor(secNum / 60)).slice(-2);
  const seconds = ('0' + (Number(secNum) - Number(minutes) * 60)).slice(-2);
  return `${minutes}:${seconds}`;
};
const WAIT_TIME = 60;

type Props = {
  otpData: OtpData;
  setOtpData: (otpData: OtpData) => void;
  currentMethod: number;
  setCurrentMethod: (method: number) => void;
};

const getSubTitle = (method: number, target: string) => {
  if (method === OtpMethod.SMS || method === OtpMethod.CALL) {
    return `Enter the 6-digit code sent to your phone number ${target}`;
  }

  return `Enter the 6-digit code sent to your email ${target}`;
};

const Otp = ({ otpData, setOtpData, currentMethod, setCurrentMethod }: Props) => {
  const dispatch = useDispatch();
  const { loading: verifyLoading, target } = otpData;
  const [otp, setOtp] = useState<string>('');
  const [time, setTime] = useState(WAIT_TIME);
  const [showLimitSession, setShowLimitSession] = useState(false);

  const { challengerId } = useSelector((state: RootState) => state.auth);

  const [warningModalData, setWarningModalData] = useState({
    open: false,
    errorMsg: '',
    title: '',
  });

  const callbackTimer = () => {
    if (time <= 1) setTime(0);
    else setTime(prev => prev - 1);
  };
  useInterval(callbackTimer, 1000);

  const clearOtpInput = () => {
    setOtp('');
    // trick to update UI
    const inputList = document.getElementsByTagName('input');
    Array.from(inputList).forEach(item => {
      // eslint-disable-next-line no-param-reassign
      item.value = '';
    });
  };

  const handleCloseWarningModal = () => {
    setWarningModalData({ ...warningModalData, open: false });
    clearOtpInput();
  };

  const resendOTP = async (method: number) => {
    try {
      const res = await dispatch(
        authResendOTPRequest({
          challengerId,
          otpMethod: method,
        }),
      );

      clearOtpInput();
      setCurrentMethod(method);
      setOtpData({ ...otpData, target: res.data?.target || '' });
      setTime(WAIT_TIME);
    } catch (e: any) {
      setWarningModalData({
        open: true,
        errorMsg: e?.data?.message || '',
        title: 'Unexpected issue happened',
      });
    }
  };

  const verifyOtp = async (requestBody: VerifyOTPBody) => {
    if (!otpData?.loading) {
      setOtpData({ ...otpData, loading: true });
      try {
        const responseVerify = await dispatch(iamVerifyPhoneRequest(requestBody));
        const verifyStatus = responseVerify.data?.status;
        if (!showLimitSession && verifyStatus === 4) {
          setShowLimitSession(true);
          dispatch(
            addModal({
              type: 'warningModal',
              id: 1,
              data: {
                content: (
                  <div className={styles.wrapPopup}>
                    <img src={IconSecure} alt="secure" />
                    <div className={styles.warningTitle}>Warning</div>
                    <div className={styles.warningContent}>
                      You are currently logged in on another device. Logging in on this device will
                      log you out from the previous device.
                    </div>
                  </div>
                ),
                size: 'tiny',
                onOk: () => {
                  dispatch(iamLoginWithLimitedDevices({ token: responseVerify.data?.magicToken }));
                  setShowLimitSession(false);
                },
                onClose: () => {
                  setShowLimitSession(false);
                },
              },
            }),
          );
          document.getElementById('limit-session-btn')?.focus();
        }
      } catch (e: any) {
        setWarningModalData({
          open: true,
          errorMsg: e?.data?.message || '',
          title: '',
        });
      } finally {
        setOtpData({ ...otpData, loading: false });
      }
    }
  };

  const handleChange = async (newOTP: string) => {
    if (otp !== newOTP) {
      setOtp(newOTP);
      if (newOTP.length === 6) await verifyOtp({ otp: newOTP, challengerId });
    }
  };

  return (
    <Form>
      <img className={styles.logo} src={IconLogin} alt="manadr login head" />
      <div className={styles.title}>Verification Code</div>
      <div className={styles.subTitle}>{getSubTitle(currentMethod, target)}</div>
      <InputOTP
        numberOnly
        value={otp}
        onChange={handleChange}
        otpLength={6}
        autoFocus
        inputClass={styles.inputStyleOTP}
        errorMessageClass={styles.inputStyleError}
      />
      <div className={styles.resend}>
        {time !== 0 ? (
          <span className={styles.subTitle}>{`Please wait for OTP (${formatTime(time)})`}</span>
        ) : (
          <>
            <div className={styles.resendOptionBtnWrapper}>
              Didn&apos;t get the code?{' '}
              <a className={styles.resendBtn} onClick={() => resendOTP(currentMethod)}>
                Resend OTP
              </a>
            </div>
            {otpMethodOptions
              .filter(option => option.value !== currentMethod)
              .map(option => (
                <div
                  key={option.value}
                  className={classNames([styles.resendOptionBtnWrapper, styles.resendBtn])}
                  onClick={() => resendOTP(option.value)}>
                  Get OTP via {option.text}
                </div>
              ))}
          </>
        )}
      </div>
      <Button
        loading={verifyLoading}
        onClick={() => verifyOtp({ otp, challengerId })}
        primary
        size="large"
        disabled={otp?.length < 6}
        style={{
          backgroundColor: '#0984e3',
          fontWeight: '600',
          fontSize: '14px',
          height: '44px',
          marginTop: '8px',
        }}>
        Verify OTP
      </Button>
      <OtpWarningModal
        open={warningModalData.open}
        errorMsg={warningModalData.errorMsg}
        title={warningModalData.title}
        onClose={handleCloseWarningModal}
      />
    </Form>
  );
};

export default Otp;
